import React, { useState, useEffect } from 'react';
import {
    Box, Container, Typography, Grid, Card, Button,
    alpha, Stack, Chip, Avatar, Divider, Paper, Fade, Grow,
} from '@mui/material';
import {
    School as SchoolIcon,
    LocationOn as LocationIcon,
    CheckCircle as CheckIcon,
    MenuBook as BookIcon,
    EmojiEvents as TrophyIcon,
    Groups as GroupsIcon,
    LaptopMac as LmsIcon,
    CloudUpload as UploadIcon,
    Assignment as CourseIcon,
    Person as PersonIcon,
    Security as SecurityIcon,
    School as CertIcon,
    Dashboard as DashboardIcon,
    ArrowBack as ArrowBackIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Navbar from '../Navbar';
import HomeFooter from '../HomeFooter';

const BRAND_GRADIENT = 'linear-gradient(135deg, #1d748b 0%, #52d370 100%)';
const AEG_COLOR = '#fd7e14';

// ==================== AEG DATA ====================
const aegData = {
    name: 'AEG International College',
    shortName: 'AEG',
    tagline: 'Empowering Future Leaders',
    color: AEG_COLOR,
    established: '2008',
    // location: 'AEG Campus, Singapore',
    phone: '+65 6456 7890',
    email: 'info@aegcollege.edu',
    website: 'www.aegcollege.edu',
    type: 'Higher Education & Professional Learning Platform',
    about: 'AEG International College is an educational institution in Singapore offering professional and higher-diploma programmes across engineering, facility management and caregiving disciplines.',
    lmsIntro: 'We developed and implemented a dedicated Learning Management System (LMS) for AEG International College to support digital learning and academic management.',
    programmes: [
        'Higher Diploma in Civil Engineering',
        'Higher Diploma in Facility Management',
        'Higher Diploma in Mechanical & Electrical Engineering',
        'Higher Diploma in Electrical Engineering',
        'Higher Diploma in Mechanical Engineering',
        'Higher Diploma in Care Giving',
    ],
    lmsFeatures: [
        {
            title: 'Student Learning Portal',
            desc: 'Provides students with a centralized platform to access their academic learning resources.',
            icon: <PersonIcon />,
        },
        {
            title: 'Course Management',
            desc: 'Organizes programmes and course-related content in a structured digital environment.',
            icon: <CourseIcon />,
        },
        {
            title: 'Learning Materials',
            desc: 'Enables students to access course materials and educational resources online.',
            icon: <BookIcon />,
        },
        {
            title: 'Student Management',
            desc: 'Supports the management of student learning activities and academic information.',
            icon: <GroupsIcon />,
        },
        {
            title: 'Online Academic Access',
            desc: 'Gives students convenient access to learning resources from a centralized platform.',
            icon: <LmsIcon />,
        },
        {
            title: 'Assessment & Learning Support',
            desc: 'Provides a digital environment for delivering and managing academic activities.',
            icon: <UploadIcon />,
        },
        {
            title: 'Certificate & Student Services',
            desc: 'Supports student-related academic and administrative processes.',
            icon: <CertIcon />,
        },
        {
            title: 'Centralized LMS Platform',
            desc: 'Brings learning resources and student activities together in one web-based system.',
            icon: <DashboardIcon />,
        },
    ],
    studentResources: [
        'Application Forms',
        'Student Contracts',
        'Advisory Information',
        'Student Request Forms',
        'Student Handbooks',
    ],
    technologies: [
        'Learning Management System (LMS)',
        'Student Portal',
        'Course Management',
        'Digital Education',
        'Academic Management',
    ],
    quickInfo: [
        { label: 'Client', value: 'AEG International College', icon: <SchoolIcon /> },
        // { label: 'Location', value: 'Singapore', icon: <LocationIcon /> },
        { label: 'Platform', value: 'AEG International College LMS', icon: <LmsIcon /> },
    ],
};

const AEGCollegePage = () => {
    const [mounted, setMounted] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        setMounted(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    if (!mounted) return null;

    return (
        <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: '#f8f9fa' }}>
            <Navbar />

            {/* ==================== HERO ==================== */}
            <Box
                sx={{
                    background: BRAND_GRADIENT,
                    pt: { xs: 8, md: 10 },
                    pb: { xs: 12, md: 16 },
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ position: 'absolute', top: '10%', right: '5%', width: 350, height: 350, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.25) 0%, transparent 70%)' }} />
                <Box sx={{ position: 'absolute', bottom: '5%', left: '5%', width: 250, height: 250, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)' }} />

                <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
                    {/* Back Button */}
                    {/* <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/colleges')}
                        sx={{
                            mb: 3,
                            color: 'white',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                        }}
                    >
                        Back to Colleges
                    </Button> */}

                    <Fade in timeout={800}>
                        <Box>
                            {/* <Chip
                                label="CLIENT PROJECT"
                                sx={{
                                    mb: 3,
                                    bgcolor: 'rgba(255,255,255,0.2)',
                                    color: 'white',
                                    fontWeight: 700,
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255,255,255,0.3)',
                                }}
                            /> */}

                            <Typography
                                variant="h1"
                                sx={{
                                    fontSize: { xs: '2.2rem', md: '3.5rem' },
                                    fontWeight: 800,
                                    mb: 2,
                                    color: 'white',
                                    lineHeight: 1.2,
                                }}
                            >
                                {aegData.name}
                            </Typography>

                            <Typography
                                variant="h6"
                                sx={{
                                    color: 'rgba(255,255,255,0.95)',
                                    maxWidth: 800,
                                    fontWeight: 400,
                                    mb: 2,
                                }}
                            >
                                {aegData.type}
                            </Typography>

                            <Stack direction="row" spacing={2} flexWrap="wrap">
                                <Chip
                                    icon={<SchoolIcon sx={{ color: 'white !important' }} />}
                                    label="Singapore"
                                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
                                />
                                <Chip
                                    icon={<LmsIcon sx={{ color: 'white !important' }} />}
                                    label="LMS Platform"
                                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
                                />
                            </Stack>
                        </Box>
                    </Fade>
                </Container>
            </Box>

            {/* ==================== OVERVIEW CARD ==================== */}
            <Container maxWidth="xl" sx={{ mt: -10, mb: 5, position: 'relative', zIndex: 2 }}>
                <Grow in timeout={1000}>
                    <Paper elevation={24} sx={{ p: { xs: 3, md: 5 }, borderRadius: 4, bgcolor: 'white' }}>
                        <Grid container spacing={4}>
                            <Grid item xs={12} md={8}>
                                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                                    {/* <Avatar
                                        sx={{
                                            width: 70,
                                            height: 70,
                                            background: AEG_COLOR,
                                            fontWeight: 800,
                                            fontSize: '1.5rem',
                                        }}
                                    >
                                        {aegData.shortName}
                                    </Avatar> */}
                                    <Box>
                                        <Typography variant="h4" sx={{ fontWeight: 800 }}>
                                            {aegData.name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {aegData.type}
                                        </Typography>
                                    </Box>
                                </Stack>

                                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                                    {aegData.about}
                                </Typography>

                                <Typography variant="body1" color="text.secondary">
                                    {aegData.lmsIntro}
                                </Typography>
                            </Grid>

                            {/* Quick Info */}
                            <Grid item xs={12} md={4}>
                                <Stack spacing={2}>
                                    {aegData.quickInfo.map((info) => (
                                        <Paper
                                            key={info.label}
                                            sx={{ p: 2, bgcolor: alpha(AEG_COLOR, 0.08), borderRadius: 2 }}
                                        >
                                            <Stack direction="row" spacing={2} alignItems="center">
                                                <Box sx={{ color: AEG_COLOR }}>{info.icon}</Box>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {info.label}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                        {info.value}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </Paper>
                                    ))}
                                </Stack>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grow>
            </Container>

            {/* ==================== PROGRAMMES OFFERED ==================== */}
            <Container maxWidth="xl" sx={{ py: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                    Programmes Offered
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                    Professional and higher-diploma programmes at AEG International College
                </Typography>

                <Grid container spacing={3}>
                    {aegData.programmes.map((programme, i) => (
                        <Grid item xs={12} sm={6} md={4} key={programme}>
                            <Grow in timeout={(i + 1) * 100}>
                                <Card
                                    sx={{
                                        p: 3,
                                        borderRadius: 3,
                                        height: '100%',
                                        boxShadow: '0 8px 30px rgba(0,0,0,0.06)',
                                        borderLeft: `4px solid ${AEG_COLOR}`,
                                        transition: 'all 0.25s',
                                        '&:hover': {
                                            transform: 'translateY(-6px)',
                                            boxShadow: `0 20px 40px ${alpha(AEG_COLOR, 0.2)}`,
                                        },
                                    }}
                                >
                                    <Stack direction="row" spacing={2} alignItems="flex-start">
                                        <Avatar sx={{ bgcolor: alpha(AEG_COLOR, 0.12), color: AEG_COLOR }}>
                                            <BookIcon />
                                        </Avatar>
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                            {programme}
                                        </Typography>
                                    </Stack>
                                </Card>
                            </Grow>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* ==================== LMS FEATURES ==================== */}
            <Box sx={{ bgcolor: '#f8f9fa', py: 6 }}>
                <Container maxWidth="xl">
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                        <LmsIcon sx={{ color: AEG_COLOR, fontSize: 36 }} />
                        <Typography variant="h4" sx={{ fontWeight: 800 }}>
                            LMS Features
                        </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                        Key modules we developed for the AEG International College learning platform
                    </Typography>

                    <Grid container spacing={3}>
                        {aegData.lmsFeatures.map((feature, i) => (
                            <Grid item xs={12} sm={6} md={3} key={feature.title}>
                                <Grow in timeout={(i + 1) * 100}>
                                    <Card
                                        sx={{
                                            p: 3,
                                            borderRadius: 3,
                                            height: '100%',
                                            boxShadow: '0 8px 30px rgba(0,0,0,0.06)',
                                            transition: 'all 0.25s',
                                            '&:hover': {
                                                transform: 'translateY(-6px)',
                                                boxShadow: `0 20px 40px ${alpha(AEG_COLOR, 0.2)}`,
                                            },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 52,
                                                height: 52,
                                                borderRadius: 2,
                                                bgcolor: alpha(AEG_COLOR, 0.12),
                                                color: AEG_COLOR,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mb: 2,
                                            }}
                                        >
                                            {feature.icon}
                                        </Box>
                                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                                            {feature.title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {feature.desc}
                                        </Typography>
                                    </Card>
                                </Grow>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* ==================== STUDENT RESOURCES + TECHNOLOGIES ==================== */}
            <Container maxWidth="xl" sx={{ py: 6 }}>
                <Grid container spacing={4}>
                    {/* Student Resources */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 4, borderRadius: 3, height: '100%' }}>
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                                <PersonIcon sx={{ color: AEG_COLOR, fontSize: 32 }} />
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                    Student Resources
                                </Typography>
                            </Stack>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Student-oriented resources provided by the college
                            </Typography>
                            <Stack spacing={2}>
                                {aegData.studentResources.map((resource) => (
                                    <Stack key={resource} direction="row" spacing={1.5} alignItems="center">
                                        <CheckIcon sx={{ color: AEG_COLOR }} />
                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                            {resource}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Stack>
                        </Paper>
                    </Grid>

                    {/* Technologies */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 4, borderRadius: 3, height: '100%' }}>
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                                <SecurityIcon sx={{ color: AEG_COLOR, fontSize: 32 }} />
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                    Technology & Solution
                                </Typography>
                            </Stack>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Technologies used to build the AEG LMS platform
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {aegData.technologies.map((tech) => (
                                    <Chip
                                        key={tech}
                                        label={tech}
                                        sx={{
                                            bgcolor: alpha(AEG_COLOR, 0.12),
                                            color: AEG_COLOR,
                                            fontWeight: 600,
                                            mb: 1,
                                        }}
                                    />
                                ))}
                            </Stack>
                        </Paper>
                    </Grid>
                </Grid>
            </Container>



        </Box>
    );
};

export default AEGCollegePage;